import React, { useState, useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ArrowLeft, Building2, Users, Shield, Eye,
  LogIn, Globe, Calendar, Tag, Send,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

interface TenantUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  userType: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface TenantDetail {
  id: number;
  name: string;
  status: string;
  domain: string;
  trackingDomain: string;
  plan: string;
  defaultCurrencyId: string;
  createdAt: string;
  updatedAt: string;
}

const planOptions = ["free", "starter", "pro", "enterprise"];
const statusColors: Record<string, string> = {
  active: "bg-green-50 text-green-700",
  pending: "bg-amber-50 text-amber-700",
  suspended: "bg-red-50 text-red-700",
};

export function TenantDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { login } = useAuth();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [resending, setResending] = useState(false);
  const [planValue, setPlanValue] = useState("");
  const [savingPlan, setSavingPlan] = useState(false);

  async function fetchTenant() {
    setLoading(true);
    try {
      const res = await api.get(`/v1/super-admin/tenants/${id}`);
      const data = await res.json();
      setTenant(data.tenant);
      setTenantUsers(data.users || []);
      setPlanValue(data.tenant?.plan || "free");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTenant();
  }, [id]);

  async function handleImpersonate() {
    if (!confirm("You will be logged in as this tenant's admin. Continue?")) return;
    setImpersonating(true);
    try {
      const res = await api.post(`/v1/super-admin/impersonate/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Impersonation failed");
      login(data.token, data.refreshToken || "");
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      setImpersonating(false);
    }
  }

  async function handlePlanChange() {
    setSavingPlan(true);
    try {
      await api.patch(`/v1/super-admin/tenants/${id}/plan`, { plan: planValue });
      await fetchTenant();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingPlan(false);
    }
  }

  async function handleStatusToggle() {
    if (!tenant) return;
    const newStatus = tenant.status === "active" ? "suspended" : "active";
    if (!confirm(`${newStatus === "suspended" ? "Suspend" : "Activate"} this tenant?`)) return;
    try {
      await api.patch(`/v1/super-admin/tenants/${id}/status`, { status: newStatus });
      await fetchTenant();
    } catch (err) {
      console.error(err);
    }
  }

  async function handleResendInvite() {
    if (!confirm("Resend the invite email to the tenant admin?")) return;
    setResending(true);
    try {
      const res = await api.post(`/v1/super-admin/tenants/${id}/resend-invite`);
      const data = await res.json() as any;
      if (!res.ok) throw new Error(data.error || "Failed to resend invite");
      alert(data.message || "Invite email re-sent!");
    } catch (err: any) {
      alert(err.message || "Failed to resend invite");
    } finally {
      setResending(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-24 text-gray-500">Tenant not found</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/super-admin/tenants"
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{tenant.name}</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Tenant ID: {tenant.id}
          </p>
        </div>
        <span
          className={cn(
            "inline-flex rounded-full px-3 py-1 text-sm font-medium capitalize",
            statusColors[tenant.status] || "bg-gray-100 text-gray-600",
          )}
        >
          {tenant.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Details
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Domain:</span>
              <span className="font-medium">{tenant.domain || "Not set"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Plan:</span>
              <span className="font-medium capitalize">{tenant.plan}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Created:</span>
              <span className="font-medium">
                {new Date(tenant.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Actions card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Actions
          </h2>
          <div className="space-y-3">
            <button
              onClick={handleImpersonate}
              disabled={impersonating || tenant.status !== "active"}
              className="w-full flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              <LogIn className="w-4 h-4" />
              {impersonating ? "Logging in..." : "Impersonate Admin"}
            </button>
            <button
              onClick={handleStatusToggle}
              className={cn(
                "w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors",
                tenant.status === "active"
                  ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                  : "bg-green-50 text-green-700 hover:bg-green-100",
              )}
            >
              {tenant.status === "active" ? "Suspend Tenant" : "Activate Tenant"}
            </button>
            <button
              onClick={handleResendInvite}
              disabled={resending}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {resending ? "Sending..." : "Resend Invite Email"}
            </button>
          </div>
        </div>

        {/* Plan card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Billing Plan
          </h2>
          <select
            value={planValue}
            onChange={(e) => setPlanValue(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          >
            {planOptions.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={handlePlanChange}
            disabled={savingPlan || planValue === tenant.plan}
            className="w-full rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {savingPlan ? "Saving..." : "Update Plan"}
          </button>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> Users ({tenantUsers.length})
          </h2>
        </div>
        {tenantUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Users className="w-8 h-8 mb-2 text-gray-300" />
            <p className="text-sm">No users in this tenant</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tenantUsers.map((u) => (
                <tr key={u.id} className={cn("hover:bg-gray-50/50", !u.isActive && "opacity-50")}>
                  <td className="px-6 py-3">
                    <p className="text-sm font-medium text-gray-900">
                      {u.firstName && u.lastName
                        ? `${u.firstName} ${u.lastName}`
                        : "Pending invite"}
                    </p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
                        u.role === "admin"
                          ? "bg-purple-50 text-purple-700"
                          : "bg-blue-50 text-blue-700",
                      )}
                    >
                      {u.role === "admin" ? (
                        <Shield className="w-3 h-3" />
                      ) : (
                        <Eye className="w-3 h-3" />
                      )}
                      {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={cn(
                        "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                        u.isActive
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-500",
                      )}
                    >
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleDateString()
                      : "Never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
